import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Chip,
  IconButton,
  Box,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getMonitoringUsers } from '../../services/adminApi';
import type { MonitoringUserDto } from '../../types/publication';

interface MonitoringUsersDialogProps {
  open: boolean;
  onClose: () => void;
  nationalIdId: string | null;
  nationalId: string | null;
}

const MonitoringUsersDialog: React.FC<MonitoringUsersDialogProps> = ({
  open,
  onClose,
  nationalIdId,
  nationalId,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [users, setUsers] = useState<MonitoringUserDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && nationalIdId && token) {
      loadMonitoringUsers();
    }
  }, [open, nationalIdId, token]);

  const loadMonitoringUsers = async () => {
    if (!nationalIdId || !token) return;

    try {
      setLoading(true);
      setError(null);

      const data = await getMonitoringUsers(nationalIdId, token);
      setUsers(data);
    } catch (err: any) {
      console.error('Failed to load monitoring users:', err);
      setError(t('admin.publications.monitoringUsers.errors.loadFailed'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (accountId: string) => {
    navigate(`/${i18n.language}/admin/users/${accountId}`);
    onClose();
  };

  const handleRowClick = (params: GridRowParams) => {
    handleViewUser(params.row.accountId);
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: t('admin.publications.monitoringUsers.columns.email'),
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'label',
      headerName: t('admin.publications.monitoringUsers.columns.label'),
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => params.value || '—',
    },
    {
      field: 'isAccountActive',
      headerName: t('admin.publications.monitoringUsers.columns.accountStatus'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={
            params.value
              ? t('admin.publications.monitoringUsers.status.active')
              : t('admin.publications.monitoringUsers.status.inactive')
          }
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'isMonitorActive',
      headerName: t('admin.publications.monitoringUsers.columns.monitorStatus'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={
            params.value
              ? t('admin.publications.monitoringUsers.status.active')
              : t('admin.publications.monitoringUsers.status.inactive')
          }
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'accountCreatedAt',
      headerName: t('admin.publications.monitoringUsers.columns.accountCreated'),
      width: 150,
      valueFormatter: (value: string) =>
        new Date(value).toLocaleDateString(i18n.language),
    },
    {
      field: 'monitorCreatedAt',
      headerName: t('admin.publications.monitoringUsers.columns.monitorCreated'),
      width: 150,
      valueFormatter: (value: string) =>
        new Date(value).toLocaleDateString(i18n.language),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleViewUser(params.row.accountId);
          }}
        >
          {t('admin.publications.monitoringUsers.actions.viewUser')}
        </Button>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              {t('admin.publications.monitoringUsers.title', { nationalId: nationalId || '' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin.publications.monitoringUsers.subtitle')}
            </Typography>
          </Box>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.monitoredIdentityId}
            onRowClick={handleRowClick}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
            localeText={{
              noRowsLabel: t('admin.publications.monitoringUsers.empty'),
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('admin.publications.monitoringUsers.actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonitoringUsersDialog;
